import { T, useT } from '@transifex/react';
import { Heading, HStack, Text, VStack } from '@vidahealth/vida-ui';
import { useEffect, useState } from 'react';
import { type Control, useForm, useWatch } from 'react-hook-form';
import { View } from 'react-native';

import { login } from '@/modules/api';
import { useRulesEngine } from '@/modules/rulesEngine';
import { BottomSheet, CtaButton, SensitiveField, TextField } from '@/modules/ui';

type SignInBottomSheetProps = {
  isOpen: boolean;
  onClose: () => void;
};

type SignInForm = {
  email: string;
  password: string;
  twoFactorCode: string;
};

/**
 * Sheet-based sign-in. Sits as a state-driven bottom sheet over whichever
 * screen invoked it — no route change, no URL update, no browser-history
 * pollution. Replaces the prior `/sign-in` route.
 *
 * Form state lives in react-hook-form (not useState) so a keystroke re-renders
 * only that field's Controller — not the whole sheet. Re-rendering the sheet on
 * every character re-applied each input's controlled `value`, which on
 * react-native-web resets the caret to the end mid-edit.
 */
export function SignInBottomSheet({ isOpen, onClose }: SignInBottomSheetProps) {
  const t = useT();

  const { control, handleSubmit, setValue, reset } = useForm<SignInForm>({
    defaultValues: { email: '', password: '', twoFactorCode: '' },
  });

  const [needsTwoFactor, setNeedsTwoFactor] = useState(false);
  const [lastFourDigits, setLastFourDigits] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { refreshUser } = useRulesEngine();

  // Reset all sensitive form state when the sheet closes so a subsequent open
  // never shows stale credentials, 2FA state, or error messages from a prior
  // attempt.
  useEffect(() => {
    if (!isOpen) {
      reset();
      setNeedsTwoFactor(false);
      setLastFourDigits(null);
      setErrorMessage(null);
    }
  }, [isOpen, reset]);

  const onSubmit = handleSubmit(async ({ email, password, twoFactorCode }) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const result = await login(
        email.trim(),
        password,
        needsTwoFactor ? twoFactorCode.trim() : undefined
      );

      if (result.status === 202) {
        setNeedsTwoFactor(true);
        setLastFourDigits(result.data.lastFourDigits);
        setValue('twoFactorCode', '');
        return;
      }

      await refreshUser();

      onClose();
    } catch (error) {
      const message =
        error && typeof error === 'object' && 'response' in error
          ? t('Unable to sign in with those credentials.')
          : t('Something went wrong. Please try again.');
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      stickyHeader={
        <Heading size='lg' className='font-heading-semibold text-primary-800 dark:text-primary-0'>
          <T _str='Sign in' />
        </Heading>
      }
    >
      <VStack space='2xl'>
        <Text size='sm' className='text-typography-700'>
          <T _str='Sign in for onboarding development.' />
        </Text>

        <VStack space='lg'>
          <TextField
            control={control}
            name='email'
            placeholder={t('Email address')}
            autoCapitalize='none'
            autoComplete='email'
            keyboardType='email-address'
            inputMode='email'
            textContentType='emailAddress'
            editable={!needsTwoFactor && !isSubmitting}
            testID='sign-in-email'
          />
          <SensitiveField
            control={control}
            name='password'
            placeholder={t('Password')}
            autoComplete='current-password'
            textContentType='password'
            editable={!needsTwoFactor && !isSubmitting}
            testID='sign-in-password'
          />
          {needsTwoFactor ? (
            <TextField
              control={control}
              name='twoFactorCode'
              placeholder={t('Verification code')}
              autoCapitalize='none'
              autoComplete='one-time-code'
              keyboardType='number-pad'
              inputMode='numeric'
              textContentType='oneTimeCode'
              editable={!isSubmitting}
              testID='sign-in-code'
            />
          ) : null}
        </VStack>

        {needsTwoFactor ? (
          <Text size='sm' className='text-typography-700'>
            {lastFourDigits ? (
              <T
                _str='Enter the code sent to the phone number ending in {digits}.'
                digits={lastFourDigits}
              />
            ) : (
              <T _str='Enter the verification code sent to your phone.' />
            )}
          </Text>
        ) : null}

        {errorMessage ? (
          <Text size='sm' className='text-error-700'>
            {errorMessage}
          </Text>
        ) : null}

        <HStack space='lg'>
          <View className='flex-1' />

          <SignInSubmitButton
            control={control}
            needsTwoFactor={needsTwoFactor}
            isSubmitting={isSubmitting}
            onPress={onSubmit}
          />
        </HStack>
      </VStack>
    </BottomSheet>
  );
}

/**
 * The submit button watches the form values to drive its disabled state. Kept as
 * a leaf so the per-keystroke re-render from `useWatch` is scoped here (no text
 * input → no caret concerns) and never reaches the fields above.
 */
function SignInSubmitButton({
  control,
  needsTwoFactor,
  isSubmitting,
  onPress,
}: {
  control: Control<SignInForm>;
  needsTwoFactor: boolean;
  isSubmitting: boolean;
  onPress: () => void;
}) {
  const [email, password, twoFactorCode] = useWatch({
    control,
    name: ['email', 'password', 'twoFactorCode'],
  });

  const canSubmit = (email ?? '').trim().length > 0 && (password ?? '').length > 0;
  const canSubmitTwoFactor = canSubmit && (twoFactorCode ?? '').trim().length > 0;
  const isDisabled = isSubmitting || (needsTwoFactor ? !canSubmitTwoFactor : !canSubmit);

  return (
    <CtaButton
      isBusy={isSubmitting}
      isDisabled={isDisabled}
      onPress={onPress}
      testID='sign-in-submit'
    >
      {needsTwoFactor ? <T _str='Verify and continue' /> : <T _str='Sign in' />}
    </CtaButton>
  );
}
